import { Link } from "react-router-dom";
import HeroSection from "./components/HeroSection";
import DramaCard from "@/components/base/DramaCard";
import SectionHeading from "@/components/base/SectionHeading";
import usePageMeta from "@/hooks/usePageMeta";
import { getAllDramas, getAllNews } from "@/lib/queries";

const quickEntries = [
  {
    icon: "ri-play-list-add-line",
    title: "最新电视剧",
    desc: "正在热播与最新上线的金瓶梅系列剧集",
    to: "/category/series?status=连载",
    tone: "bg-primary-600",
  },
  {
    icon: "ri-history-line",
    title: "经典重温",
    desc: "已完结的口碑之作，值得一再回味",
    to: "/category/series?status=完结",
    tone: "bg-secondary-700",
  },
  {
    icon: "ri-user-star-line",
    title: "热门演员",
    desc: "系列剧集中的实力派与人气演员资料",
    to: "/actor/a2",
    tone: "bg-accent-600",
  },
];

export default function Home() {
  usePageMeta({
    title: "金瓶梅3影视大全 - 最新电视剧与剧情介绍",
    description:
      "金瓶梅3影视大全为您提供最新电视剧集、剧情介绍及演员信息，覆盖金瓶梅系列相关影视作品，助您轻松了解剧情动态和观看指南。",
    keywords: "金瓶梅3,金瓶梅3电视剧,金瓶梅3剧情,金瓶梅3演员,电视剧推荐,影视剧资讯,剧情介绍",
  });

  const allDramas = getAllDramas();
  const featured = [...allDramas].sort((a, b) => b.heat - a.heat).slice(0, 5);
  const hot = [...allDramas].sort((a, b) => b.heat - a.heat).slice(0, 5);
  const latest = [...allDramas].sort((a, b) => (a.updateTime < b.updateTime ? 1 : -1)).slice(0, 8);
  const newsList = getAllNews().slice(0, 4);

  return (
    <div className="w-full">
      <HeroSection featured={featured} hot={hot} />

      <section className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {quickEntries.map((entry) => (
            <Link
              key={entry.title}
              to={entry.to}
              className="group flex items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-primary-300"
            >
              <span
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-xl text-background-50 ${entry.tone}`}
              >
                <i className={entry.icon} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                  {entry.title}
                  <i className="ri-arrow-right-up-line text-sm text-foreground-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
                <span className="mt-1.5 block text-[13px] leading-6 text-foreground-600">
                  {entry.desc}
                </span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <SectionHeading
            eyebrow="精选剧集"
            title="金瓶梅系列精选剧集"
            description="汇集系列中关注度较高的电视剧作品，点击可查看完整剧情介绍、演员表与相关推荐。"
            moreTo="/category/series"
            moreLabel="进入分类页"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
            {latest.map((drama) => (
              <DramaCard key={drama.id} drama={drama} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        <SectionHeading
          eyebrow="影视资讯"
          title="金瓶梅系列影视动态"
          description="剧集更新、幕后制作与专题解读，第一时间了解系列作品的最新进展。"
          moreTo="/news"
          moreLabel="更多新闻"
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
          {newsList.map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.id}`}
              className="group flex gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
            >
              <div className="h-24 w-32 shrink-0 overflow-hidden rounded-md md:h-28 md:w-40">
                <img
                  src={item.cover}
                  alt={`${item.title} 新闻配图`}
                  title={`${item.title} - 金瓶梅3影视资讯`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="min-w-0 flex-1 py-1">
                <div className="flex items-center gap-2 text-[11px] font-label text-foreground-400">
                  <span className="rounded-full bg-accent-100 px-2 py-0.5 text-accent-900">
                    {item.category}
                  </span>
                  <span>{item.date}</span>
                </div>
                <h3 className="mt-2 line-clamp-2 font-heading text-[15px] font-semibold leading-6 text-foreground-950 transition-colors group-hover:text-primary-600">
                  {item.title}
                </h3>
                <p className="mt-1.5 line-clamp-2 text-xs leading-6 text-foreground-600">
                  {item.summary}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}